import { UploadsApi, type UploadPhase } from "@/api/uploads.api";
import { $selectedImage } from "@/stores/selectedImage";
import { UploadsService } from "@/stores/uploads";
import { toast } from "@/utils/toast";
import { usePending } from "@/utils/hooks/usePending.js";
import {
  Box,
  CloseButton,
  Group,
  Loader,
  Progress,
  Stack,
  Text,
  rem,
} from "@mantine/core";
import { Dropzone, type FileWithPath } from "@mantine/dropzone";
import { IconUpload, IconX } from "@tabler/icons-react";
import { useState } from "react";
export const MAX_FILE_SIZE_MB = 500;

export const IMAGE_MIME_TYPE = [
  "image/jpg",
  "image/jpeg",
  "image/png",
  "image/tif",
  "image/tiff",
  "image/webp",
  "image/heic",
  "image/heif",
];

type FileProgress = {
  name: string;
  phase: UploadPhase;
  percent: number;
  elapsedMs?: number;
  error?: string;
};

function formatElapsed(ms: number) {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

function isProcessing(item: FileProgress) {
  return !item.error && item.phase === "processing";
}

function statusLabel(item: FileProgress) {
  if (item.error) return item.error;
  switch (item.phase) {
    case "preparing":
      return "Preparing…";
    case "uploading":
      return `Uploading — ${item.percent}%`;
    case "processing":
      // No percentage exists for this phase (see UploadProgress in
      // uploads.api.ts). The elapsed clock is what proves it isn't frozen.
      return `Processing your image… ${formatElapsed(item.elapsedMs || 0)}`;
  }
}

export const Drop = () => {
  // One row per file. Uploads run in parallel, so each row tracks its own
  // phase; rows for successful files clear on completion, rows for failed
  // files STAY until dismissed. A toast would auto-dismiss, and the failure
  // that matters most here can arrive four minutes after the drop — long
  // after the customer has looked away. 2026-07-26.
  const [items, setItems] = useState<FileProgress[]>([]);

  const dismiss = (index: number) =>
    setItems(prev => prev.filter((_, i) => i !== index));

  const uploadFiles = usePending(async (files: FileWithPath[]) => {
    setItems(
      files.map(file => ({
        name: file.name,
        phase: "preparing" as UploadPhase,
        percent: 0,
      }))
    );

    const update = (index: number, patch: Partial<FileProgress>) =>
      setItems(prev =>
        prev.map((item, i) => (i === index ? { ...item, ...patch } : item))
      );

    const uploadPromises = files.map((file, index) =>
      UploadsApi.uploadFileToS3(file, {
        onProgress: progress =>
          update(index, {
            phase: progress.phase,
            percent: progress.percent,
            elapsedMs: progress.elapsedMs,
          }),
      })
    );
    const results = await Promise.allSettled(uploadPromises);

    // Failed uploads used to vanish silently here — tell the customer
    // (required feedback per 2026-07-15 decision; Sentry 1S).
    const failedIndexes: number[] = [];
    results.forEach((result, index) => {
      if (result.status !== "rejected") return;
      failedIndexes.push(index);
      (window as any).Sentry?.captureException(result.reason);
      update(index, {
        error:
          result.reason instanceof Error && result.reason.message
            ? result.reason.message
            : "Upload failed — please try again.",
      });
    });

    if (failedIndexes.length) {
      // The toast draws the eye; the row underneath carries the actual reason
      // and the flattened-TIFF advice, and it persists.
      toast.show({
        message: `${failedIndexes.length} of ${files.length} file(s) failed to upload — details below.`,
        color: "red",
      });
      setItems(prev => prev.filter((_, i) => failedIndexes.includes(i)));
    } else {
      setItems([]);
    }

    const uploads = await UploadsService.getUploads();
    const lastUpload = uploads.at(0);
    if (lastUpload) {
      $selectedImage.set(lastUpload);
    }
  });

  const isUploading = uploadFiles.isPending;

  return (
    <Dropzone
      accept={IMAGE_MIME_TYPE}
      h="100%"
      maxSize={MAX_FILE_SIZE_MB * 1000000}
      // Previously `loading={uploadFiles.isPending}`, which drew a spinner
      // overlay across the whole zone — that spinner IS the problem this
      // change removes, and it would cover the progress rows. Re-entry is
      // guarded explicitly instead, since usePending does not guard it.
      activateOnClick={!isUploading}
      onDrop={files => {
        if (isUploading) return;
        uploadFiles(files);
      }}
      onReject={files => {
        console.log("rejected files", files);
        toast.show({
          message: `That file type or size isn't supported — please use JPEG, PNG, TIFF, WEBP or HEIC under ${MAX_FILE_SIZE_MB}MB.`,
          color: "red",
        });
      }}
      style={{
        "--_dropzone-border-color": "var(--mantine-color-giclee-5)",
        borderWidth: rem(2.33),
      }}
      w="100%"
    >
      {items.length > 0 ? (
        <Stack
          gap="xs"
          justify="center"
          mih={175}
          px="md"
          py="sm"
          style={{ maxHeight: rem(190), overflowY: "auto" }}
        >
          {items.map((item, index) => (
            <Box key={`${item.name}-${index}`}>
              <Group gap="xs" justify="space-between" wrap="nowrap">
                <Text c="gray.7" size="xs" truncate="end" style={{ flex: 1 }}>
                  {item.name}
                </Text>
                {item.error && (
                  <CloseButton
                    aria-label="Dismiss"
                    size="xs"
                    onClick={() => dismiss(index)}
                  />
                )}
              </Group>
              {isProcessing(item) ? (
                // No bar here, deliberately. A bar implies a measurable
                // quantity and this phase has none — the poll is a yes/no
                // question, so any fill level would be invented. A full teal
                // bar also read as "finished" while the label said otherwise.
                // The height is RESERVED so the label and hint below do not
                // jump when the phase flips. 2026-07-26.
                <Box h={rem(6)} />
              ) : (
                <Progress
                  color={item.error ? "red" : "giclee"}
                  size="sm"
                  value={item.error ? 100 : item.percent}
                />
              )}
              <Group gap={7} mt={4} wrap="nowrap">
                {isProcessing(item) && <Loader color="giclee" size="xs" />}
                <Text
                  c={item.error ? "red" : isProcessing(item) ? "gray.7" : "gray.6"}
                  size="xs"
                >
                  {statusLabel(item)}
                </Text>
              </Group>
            </Box>
          ))}
          {!items.some(item => item.error) && (
            <Text c="gray.5" size="xs" ta="center">
              Large files can take a few minutes — you can leave this open.
            </Text>
          )}
        </Stack>
      ) : (
        <Stack align="center" gap="md" justify="center" mih={175}>
          <Dropzone.Accept>
            <IconUpload
              stroke={2}
              style={{
                color: "var(--mantine-color-giclee-5)",
                height: rem(24),
                width: rem(24),
              }}
            />
          </Dropzone.Accept>
          <Dropzone.Reject>
            <IconX
              stroke={2}
              style={{
                color: "var(--mantine-color-giclee-5)",
                height: rem(24),
                width: rem(24),
              }}
            />
          </Dropzone.Reject>
          <Dropzone.Idle>
            <IconUpload
              stroke={2}
              style={{
                color: "var(--mantine-color-giclee-5)",
                height: rem(24),
                width: rem(24),
              }}
            />
          </Dropzone.Idle>

          <Text c="giclee" inline size="lg" ta="center">
            Upload or drop image(s)
          </Text>
        </Stack>
      )}
    </Dropzone>
  );
};
